import { useMemo } from 'react';

import { toast } from 'react-toastify';

import { useFilter } from '@features/table-filter';

import {
  useCopyPolicyMutation,
  useCreatePolicyMutation,
  useDeletePolicyMutation,
  useGetPoliciesQuery,
  useReorderPoliciesMutation,
  useReorderRulesMutation,
  useUpdatePolicyMutation,
} from '../api';
import { IPolicyFilter } from '../model/types';

const ROUTER_REORDER_SET_CHANGED = 'ROUTER_REORDER_SET_CHANGED';
const SET_CHANGED_MESSAGE = 'The list changed since it was loaded. Refresh to get the current items.';

// The reorder endpoints return 409 with a machine `code` when the submitted set is
// stale. Key off the code, not the human-readable message (per CSP-338 spec).
const isReorderSetChangedError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  const { status, data } = error as { status?: unknown; data?: unknown };
  if (status !== 409 || typeof data !== 'object' || data === null) return false;
  return (data as { code?: unknown }).code === ROUTER_REORDER_SET_CHANGED;
};

export const usePolicies = () => {
  const filter = useFilter<IPolicyFilter>({ disableUrlFilter: true });
  const { data, isLoading, isError, error, isFetching, refetch } = useGetPoliciesQuery(
    filter.currentFilter.toString(),
  );
  const [deletePolicy] = useDeletePolicyMutation();
  const [updatePolicy] = useUpdatePolicyMutation();
  const [createPolicy] = useCreatePolicyMutation();
  const [copyPolicy] = useCopyPolicyMutation();
  const [reorderPoliciesRequest] = useReorderPoliciesMutation();
  const [reorderRulesRequest] = useReorderRulesMutation();

  const orderedPolicies = useMemo(() => {
    const normalized = (data?.data ?? []).map(policy => ({
      ...policy,
      conditions: policy.conditions ?? [],
      rules: policy.rules ?? [],
    }));
    return [...normalized].sort((a, b) => a.priority - b.priority);
  }, [data?.data]);

  const clonePolicy = async (id: string) => {
    await copyPolicy({ id }).unwrap();
    refetch();
  };

  const reorderRules = async (policyId: string, ruleIds: string[]) => {
    if (!ruleIds.length) return;
    try {
      await reorderRulesRequest({ policy: policyId, order: ruleIds }).unwrap();
      toast.success('Rules priority updated');
    } catch (error) {
      // Errors are fully handled here (toast); do NOT rethrow — the drop handler is
      // fire-and-forget, so a rethrow would surface as an unhandled rejection (dev
      // overlay / Sentry noise) for an already-handled condition. The rules list is
      // reloaded by the endpoint's onQueryStarted on failure.
      toast.error(
        isReorderSetChangedError(error) ? SET_CHANGED_MESSAGE : 'Failed to update rules priority',
      );
    }
  };

  const reorderPolicies = async (ids: string[]) => {
    if (!ids.length) return;
    try {
      await reorderPoliciesRequest({ order: ids }).unwrap();
      toast.success('Policies priority updated');
      refetch();
    } catch (error) {
      // Reload the list so it reflects the server's current set (and resets the
      // optimistic order). Handled in place — see reorderRules on why we don't rethrow.
      refetch();
      toast.error(
        isReorderSetChangedError(error) ? SET_CHANGED_MESSAGE : 'Failed to update policies priority',
      );
    }
  };

  return {
    policies: orderedPolicies,
    meta: data?.meta,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
    filter,
    createPolicy,
    updatePolicy,
    deletePolicy,
    clonePolicy,
    reorderRules,
    reorderPolicies,
  };
};
